/*
 * Dots for a scroll-snap row.
 *
 * The scrolling itself stays native — this only keeps the dots in step, lets a
 * dot scroll to its item, and optionally advances on a timer. It goes quiet
 * whenever the row is not actually scrollable (desktop, or few enough items).
 */
class LusheScrollSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('ul');
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.items = this.track ? [...this.track.children] : [];
    if (!this.track || this.items.length < 2) return;

    this.index = 0;
    this.interval = (parseInt(this.dataset.interval, 10) || 4) * 1000;
    this.allowAuto =
      this.dataset.auto !== 'false' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.scrollTo(i);
        this.restart();
      })
    );

    this.track.addEventListener('scroll', () => {
      clearTimeout(this.settle);
      this.settle = setTimeout(() => this.readPosition(), 90);
    }, { passive: true });

    // A finger on the row should win over the timer, and keep winning for a beat.
    ['pointerdown', 'touchstart'].forEach((evt) =>
      this.track.addEventListener(evt, () => this.stop(), { passive: true })
    );
    ['pointerup', 'touchend', 'mouseleave'].forEach((evt) =>
      this.track.addEventListener(evt, () => this.restart(), { passive: true })
    );

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  get scrollable() {
    return this.track.scrollWidth - this.track.clientWidth > 4;
  }

  readPosition() {
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let closest = 0;
    let best = Infinity;
    this.items.forEach((item, i) => {
      const centre = item.offsetLeft + item.offsetWidth / 2;
      const gap = Math.abs(centre - mid);
      if (gap < best) {
        best = gap;
        closest = i;
      }
    });
    this.setActive(closest);
  }

  setActive(i) {
    this.index = i;
    this.dots.forEach((dot, d) => {
      dot.classList.toggle('is-active', d === i);
      dot.setAttribute('aria-selected', d === i ? 'true' : 'false');
    });
  }

  scrollTo(i) {
    const item = this.items[i];
    if (!item) return;
    this.track.scrollTo({
      left: item.offsetLeft - (this.track.clientWidth - item.offsetWidth) / 2,
      behavior: 'smooth',
    });
    this.setActive(i);
  }

  start() {
    if (!this.allowAuto || this.timer || !this.scrollable) return;
    this.timer = setInterval(() => {
      if (!this.scrollable) return this.stop();
      this.scrollTo((this.index + 1) % this.items.length);
    }, this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    // Give the swipe time to settle before the timer takes over again.
    clearTimeout(this.resume);
    this.resume = setTimeout(() => this.start(), 2500);
  }
}

customElements.define('lushe-scroll-slider', LusheScrollSlider);
